import { localePath, type Lang } from './i18n';
import { EVENT_ARCHIVE, type EvwEventRecord, type EventResult } from '../content/events/data.generated';
import { RANKINGS_OFFICIAL } from '../content/events/rankings.generated';
import headsManifest from '../content/events/heads.generated.json';
import athletesManifest from '../content/events/athletes.generated.json';
import { normName, looseName } from './names';

export { normName };

export type { EvwEventRecord, EventResult };

// Normalized name → self-hosted headshot path (scripts/scrape-event-heads.mjs).
const HEADS = headsManifest as Record<string, string>;
export const headFor = (name: string): string | null =>
  HEADS[normName(name)] || HEADS[looseName(name)] || null;

export const eastVsWestBase = (lang: Lang) => localePath(lang, '/east-vs-west');
export const eventPath = (lang: Lang, slug: string) => localePath(lang, `/east-vs-west/${slug}`);

/* ── Athlete profiles ──────────────────────────────────────────────────
   Physical stats + bio scraped once from goldsarm.com into a LOCAL data file
   (scripts/scrape-athlete-profiles.mjs). goldsarm is a data source only — these
   power our own native athlete pages; nothing links back out to it. */

interface Measure { imp: string | null; cm?: string | null; kg?: string | null; lbs?: string | null }
export interface AthleteProfile {
  slug: string;
  name: string;
  nickname: string | null;
  country: string | null;
  age: number | null;
  wins: number | null;
  losses: number | null;
  height: Measure;
  weight: Measure;
  biceps: Measure;
  forearm: Measure;
  wrist: Measure;
  bio: string[];
}
const ATHLETES = athletesManifest as unknown as Record<string, AthleteProfile>;

// Same exact-then-loose match as headFor; miss → null (avatar stays non-clickable).
export const athleteFor = (name: string): AthleteProfile | null =>
  ATHLETES[normName(name)] || ATHLETES[looseName(name)] || null;

export const athletePath = (lang: Lang, slug: string) =>
  localePath(lang, `/east-vs-west/athlete/${slug}`);

// Unique athletes (the manifest stores each profile under both exact and loose keys).
export const getAthletes = (): AthleteProfile[] => {
  const seen = new Set<string>();
  const out: AthleteProfile[] = [];
  for (const a of Object.values(ATHLETES)) {
    if (seen.has(a.slug)) continue;
    seen.add(a.slug);
    out.push(a);
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
};
export const getAthlete = (slug: string): AthleteProfile | undefined =>
  getAthletes().find((a) => a.slug === slug);

export interface AthleteBout {
  eventName: string; eventSlug: string; num: number; isoDate: string;
  opponent: string; won: boolean; score: string; arm: string; weightClass: string;
}
// Every EVW bout this athlete appears in, newest first (derived from our archive).
export const athleteRecord = (name: string): AthleteBout[] => {
  const key = normName(name);
  const rows: AthleteBout[] = [];
  for (const ev of EVENT_ARCHIVE) {
    for (const m of ev.matches) {
      const won = normName(m.winner) === key;
      const lost = normName(m.loser) === key;
      if (!won && !lost) continue;
      rows.push({
        eventName: ev.name, eventSlug: ev.slug, num: ev.num, isoDate: ev.isoDate,
        opponent: won ? m.loser : m.winner, won, score: m.score, arm: m.arm, weightClass: m.weightClass,
      });
    }
  }
  return rows.sort((a, b) => b.num - a.num);
};

export interface AthleteRank { className: string; arm: 'right' | 'left'; rank: number; champion?: boolean }
// Every official-ranking slot this athlete holds.
export const athleteRankings = (name: string): AthleteRank[] => {
  const key = normName(name);
  const out: AthleteRank[] = [];
  for (const wc of RANKINGS_OFFICIAL) {
    (['right', 'left'] as const).forEach((arm) => {
      for (const r of wc.arms[arm] || []) {
        if (!r.vacant && normName(r.name) === key) out.push({ className: wc.className, arm, rank: r.rank, champion: r.champion });
      }
    });
  }
  return out;
};

// Country → flag emoji + a cleaner display name for the goldsarm country strings.
const FLAGS: Record<string, string> = {
  armenia: '🇦🇲', australia: '🇦🇺', brazil: '🇧🇷', bulgaria: '🇧🇬', canada: '🇨🇦', croatia: '🇭🇷',
  georgia: '🇬🇪', hungary: '🇭🇺', israel: '🇮🇱', italy: '🇮🇹', kazakhstan: '🇰🇿', latvia: '🇱🇻',
  lithuania: '🇱🇹', moldova: '🇲🇩', poland: '🇵🇱', romania: '🇷🇴', russia: '🇷🇺', singapore: '🇸🇬',
  sweden: '🇸🇪', turkey: '🇹🇷', ukraine: '🇺🇦', 'united kingdom': '🇬🇧', 'united states': '🇺🇸',
};
export function displayCountry(country: string | null): string {
  if (!country) return '';
  return country.replace(/,?\s*republic of/i, '').replace(/united states of america/i, 'United States').trim();
}
export const flagFor = (country: string | null): string =>
  country ? FLAGS[displayCountry(country).toLowerCase()] || '' : '';

// Newest first (the generated file is already sorted that way, but be explicit).
export const getEvents = (): EvwEventRecord[] =>
  [...EVENT_ARCHIVE].sort((a, b) => b.num - a.num);

export const getEvent = (slug: string): EvwEventRecord | undefined =>
  EVENT_ARCHIVE.find((e) => e.slug === slug);

// The headline bout of an event — thearmwrestlingarchives lists the main event
// last, so we surface that as the card's featured result.
export const headlineMatch = (e: EvwEventRecord): EventResult | undefined =>
  e.matches[e.matches.length - 1];

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
export function formatDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  return `${MONTHS[+m[2] - 1]} ${+m[3]}, ${m[1]}`;
}
